import type { CanvasItem } from "@bedrock/domain";

const STACK_CHILD_GAP = 32;
const STACK_CHILD_ROW_GAP = 32;
const STACK_CHILD_MAX_ROW_WIDTH = 960;

interface StackRow {
  items: CanvasItem[];
  width: number;
  height: number;
}

/**
 * Places stack children on centered, bottom-aligned shelves above their stack.
 * The returned items preserve their existing order and all non-position fields.
 */
export function arrangeStackChildren(stack: CanvasItem, children: CanvasItem[]): CanvasItem[] {
  if (children.length === 0) return [];

  const rows: StackRow[] = [];
  for (const child of children) {
    const row = rows.at(-1);
    const nextWidth = row ? row.width + STACK_CHILD_GAP + child.width : child.width;
    if (!row || (row.items.length > 0 && nextWidth > STACK_CHILD_MAX_ROW_WIDTH)) {
      rows.push({ items: [child], width: child.width, height: child.height });
      continue;
    }
    row.items.push(child);
    row.width = nextWidth;
    row.height = Math.max(row.height, child.height);
  }

  const positioned = new Map<string, CanvasItem>();
  const stackCenterX = stack.x + stack.width / 2;
  let rowBottom = stack.y - STACK_CHILD_GAP;

  for (const row of rows) {
    let x = stackCenterX - row.width / 2;
    for (const child of row.items) {
      positioned.set(child.id, { ...child, x, y: rowBottom - child.height });
      x += child.width + STACK_CHILD_GAP;
    }
    rowBottom -= row.height + STACK_CHILD_ROW_GAP;
  }

  return children.map((child) => positioned.get(child.id) ?? child);
}
